import React from 'react'
import pick from 'lodash/pick'
import mapValues from 'lodash/mapValues'
import { LKDate } from 'loankeeper-common/helpers/Date'
import { Storage } from 'loankeeper-common/helpers'
import { addLogoutCallback } from '../../../store/auth'

const savedStates = {}

addLogoutCallback(() => {
  Object.keys(savedStates).forEach((key) => {
    savedStates[key] = null
  })
})

const reactForm =
  ({
    formName,
    persist = false,
    propName,
    initialValues,
    validate,
    onSubmit,
    storageFields = [],
    initializeOnSubmit = false,
  }) =>
  (Component) =>
    class WrappedForm extends React.PureComponent {
      constructor(props) {
        super(props)

        if (persist && formName && savedStates[formName]) {
          this.state = savedStates[formName]
        } else {
          this.state = this.getStateInitial()
        }
      }

      validateFn = validate || (() => ({}))

      changeHandlers = []

      componentDidMount() {
        this.ismounted = true
      }

      componentWillUnmount() {
        this.ismounted = false

        if (persist && formName) {
          if (savedStates[formName] !== null) {
            savedStates[formName] = this.state
          } else {
            delete savedStates[formName]
          }
        }
      }

      setValidateFn = (fn) => {
        if (this.validateFn !== fn) {
          this.validateFn = fn

          this.validateInternal()
        }
      }

      setChangeHandler = (names, handler) => {
        this.changeHandlers.push({ names, handler })
      }

      getStateInitial = (vals) => {
        let values =
          initialValues instanceof Function
            ? initialValues(this.props, (this.state && this.state.values) || {})
            : initialValues

        let storageValues = {}
        if (formName && storageFields.length) {
          storageValues = pick(Storage.get(formName), storageFields)
        }

        values = { ...values, ...vals, ...storageValues }

        const touched = mapValues(values, () => false)
        const errors = this.validateFn(values, this.props)
        const invalid = this.isInvalid(errors)

        return {
          dirty: false,
          pristine: true,
          submitting: false,
          values,
          touched,
          errors,
          invalid,
        }
      }

      initialize = (values, callback) => {
        this.setState(this.getStateInitial(values), () => {
          if (callback) callback(this.state.values, values)
        })
      }

      isValueValid = ({ value, fieldtype, type, integer, positive, dontParse, scale, isPersonalNo }) => {
        if (fieldtype === 'number') {
          return (
            !value ||
            (!positive && value === '-') ||
            (!isNaN(value) &&
              (!integer || !value.includes('.')) &&
              (!positive || !value.includes('-')) &&
              value.replace('.', '').replace('-', '').length < (dontParse ? 20 : 16) &&
              (!scale || !value.includes('.') || value.split('.')[1].length <= scale))
          )
        }
        if (!fieldtype && type === 'text' && isPersonalNo) {
          return (/[0-9a-zA-Z\-_]+$/.test(value) && value) || value === ''
        }

        return true
      }

      normalizeValue = ({
        name,
        type,
        value,
        checked,
        allowIndeterminate,
        radio,
        fieldtype,
        dontParse,
        isPersonalNo,
      }) => {
        let val
        if (!fieldtype && type === 'text' && isPersonalNo) {
          if (/[0-9a-zA-Z\-_]+$/.test(value) && value) {
            val = value
          } else if (value === '') {
            val = ''
          }
        } else if (fieldtype === 'number' && type === 'number') {
          if (value) {
            if (Number.isNaN(parseFloat(value))) val = 0
            else val = parseFloat(value).toFixed(0)
          } else val = '0'
        } else if (fieldtype === 'number' && value.indexOf('.') === -1) {
          const parsed = dontParse ? value.trim() : parseFloat(value)
          val = isNaN(parsed) ? value.trim() : parsed
        } else if (type === 'checkbox' && !radio) {
          const prevValue = this.state.values[name]
          val = allowIndeterminate && prevValue === false ? null : checked
        } else {
          val = value
        }

        return val
      }

      handleChange = (event, data, changeCallback, changedCallback) => {
        if (this.isValueValid(data)) {
          const value = this.normalizeValue(data)

          if (changeCallback) changeCallback(event, data, { value })
          else this.changeValue(data.name, value, changedCallback)
        }
      }

      handleDateChange = (name, value, callback) => {
        this.changeValue(name, value, callback)
      }

      isInvalid = (errors) => Object.values(errors).some((error) => error)

      changeValue = (name, value, callback) => this.changeValues({ [name]: value }, callback)

      changeValues = (newValues, callback) => {
        // here in setState we should use updater function
        // to return new state from current state
        // passed as param to updater function
        // to avoid collisions during parallel changeValues
        // instead of passing new state as param
        this.setState(
          (prevState) => {
            const values = { ...prevState.values, ...newValues }
            const errors = this.validateFn(values, this.props)

            if (formName && storageFields.length && storageFields.some((f) => newValues.hasOwnProperty(f))) {
              Storage.set(formName, pick(values, storageFields))
            }

            return {
              values,
              errors,
              dirty: true,
              pristine: false,
              invalid: this.isInvalid(errors),
              touched: { ...this.state.touched, ...mapValues(newValues, () => true) },
            }
          },
          () => (callback ? callback(this.state.values, newValues) : undefined),
        )
      }

      handleBlur = ({ target: { name } }, fieldtype) => {
        if (fieldtype === 'date') {
          const value = this.state.values[name]
          if (LKDate.isValid(value)) {
            const formatted = LKDate.from(value).toString()
            if (value !== formatted) {
              this.changeValue(name, formatted)
              return
            }
          }
        }

        this.setState({
          touched: { ...this.state.touched, [name]: true },
        })
      }

      hasError = (name) => !!this.state.errors[name] && this.state.touched[name]

      handleSubmit = async (submit) => {
        const errors = this.validateFn(this.state.values, this.props)
        const invalid = this.isInvalid(errors)
        const touched = mapValues(this.state.touched, () => true)

        if (!invalid) {
          const promise = submit ? submit(this.state.values) : onSubmit(this.state.values)

          if (promise) {
            this.setState({ submitting: true, dirty: true, pristine: false, errors, invalid, touched })

            promise
              .then(() => this.ismounted && this.initialize(initializeOnSubmit ? undefined : this.state.values))
              .catch(() => this.setState({ submitting: false }))
          }
        } else {
          this.setState({ dirty: true, pristine: false, errors, invalid, touched })

          this.focusInvalid(errors)
        }
      }

      focusInvalid = (errors) => {
        const names = Object.getOwnPropertyNames(errors).filter((name) => !!errors[name])

        names.every((name) => {
          const element = document.querySelector(`input[name=${name}],div.dropdown[name=${name}] > input.search`)

          if (element) {
            setTimeout(() => {
              if (element.focus) element.focus()
              if (element.select) element.select()
            })

            return false
          }

          return true
        })
      }

      validateInternal = () => {
        const errors = this.validateFn(this.state.values, this.props)

        const invalid = this.isInvalid(errors)

        this.setState({ errors, invalid })
      }

      handleValidate = (focus = false) => {
        const errors = this.validateFn(this.state.values, this.props)

        const invalid = this.isInvalid(errors)

        const touched = mapValues(this.state.touched, () => true)

        this.setState({ dirty: true, pristine: false, errors, invalid, touched })

        if (focus) this.focusInvalid(errors)

        return !invalid
      }

      setTouched = (touched) => {
        this.setState((state) => ({ touched: { ...state.touched, ...touched } }))
      }

      destroy = () => {
        savedStates[formName] = null
      }

      render() {
        const form = {
          values: this.state.values,
          errors: this.state.errors,
          touched: this.state.touched,
          submitting: this.state.submitting,
          invalid: this.state.invalid,
          dirty: this.state.dirty,
          pristine: this.state.pristine,

          initialize: this.initialize,
          handleChange: this.handleChange,
          handleDateChange: this.handleDateChange,
          handleBlur: this.handleBlur,
          hasError: this.hasError,
          changeValue: this.changeValue,
          changeValues: this.changeValues,
          handleSubmit: this.handleSubmit,
          setValidateFn: this.setValidateFn,
          validate: this.handleValidate,
          isValueValid: this.isValueValid,
          normalizeValue: this.normalizeValue,
          destroy: this.destroy,
          setTouched: this.setTouched,
        }

        return <Component {...this.props} {...{ [propName || 'form']: form }} />
      }
    }

export default reactForm
